import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useToteDetails } from '../hooks/useToteDetails';

interface ToteDropdownProps {
  value: string;
  onChange: (value: string) => void;
  selectedHeaderToOrg?: string;
  isDarkTheme?: boolean;
  width?: string;
  placeholder?: string;
  palletValue?: string;
  onError?: (error: string) => void;
  onToRadRequired?: () => void;
  toRadValue?: string;
  onFieldClickWithoutHeader?: () => void;
}

const ToteDropdown: React.FC<ToteDropdownProps> = ({
  value,
  onChange,
  selectedHeaderToOrg,
  isDarkTheme = false,
  width = '120px',
  placeholder = 'Enter tote',
  palletValue,
  onError,
  onToRadRequired,
  toRadValue,
  onFieldClickWithoutHeader
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: totes, refetch } = useToteDetails(selectedHeaderToOrg, true);

  const filteredTotes = useMemo(() => {
    if (!Array.isArray(totes)) return [];
    if (!search) return totes.map(t => t.tote);
    return totes.filter((t) =>
      t.tote.toLowerCase().includes(search.toLowerCase())
    ).map(t => t.tote);
  }, [totes, search]);

  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSearch('');
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || filteredTotes.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => {
        const newIndex = prev < filteredTotes.length - 1 ? prev + 1 : 0;
        setTimeout(() => {
          const dropdown = dropdownRef.current;
          const selectedItem = dropdown?.children[newIndex] as HTMLElement;
          selectedItem?.scrollIntoView({ block: 'nearest' });
        }, 0);
        return newIndex;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => {
        const newIndex = prev > 0 ? prev - 1 : filteredTotes.length - 1;
        setTimeout(() => {
          const dropdown = dropdownRef.current;
          const selectedItem = dropdown?.children[newIndex] as HTMLElement;
          selectedItem?.scrollIntoView({ block: 'nearest' });
        }, 0);
        return newIndex;
      });
    } else if (e.key === 'Enter') {
      if (selectedIndex >= 0) {
        e.preventDefault();
        const selectedTote = filteredTotes[selectedIndex];
        onChange(selectedTote);
        setShowDropdown(false);
        setSearch('');
        setSelectedIndex(-1);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSearch('');
      setSelectedIndex(-1);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={showDropdown ? search : value}
        onChange={(e) => {
          if (!selectedHeaderToOrg) {
            onFieldClickWithoutHeader?.();
            return;
          }
          const inputValue = e.target.value;
          setSearch(inputValue);
          if (!showDropdown) {
            setShowDropdown(true);
          }
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (!selectedHeaderToOrg) {
            onFieldClickWithoutHeader?.();
            return;
          }
          if (!toRadValue || toRadValue.trim() === '') {
            onToRadRequired?.();
            return;
          }
          if (!palletValue || palletValue.trim() === '') {
            onError?.('Pallet is required');
            return;
          }
          setSearch(value);
          setShowDropdown(true);
          setSelectedIndex(-1);
          if (selectedHeaderToOrg) {
            refetch(selectedHeaderToOrg);
          }
        }}
        onClick={() => {
          if (!selectedHeaderToOrg) {
            onFieldClickWithoutHeader?.();
            return;
          }
          if (!toRadValue || toRadValue.trim() === '') {
            onToRadRequired?.();
            return;
          }
          if (!palletValue || palletValue.trim() === '') {
            onError?.('Pallet is required');
            return;
          }
          setSearch(value);
          setShowDropdown(true);
          setSelectedIndex(-1);
        }}
        placeholder={placeholder}
        style={{
          width,
          padding: '8px 30px 8px 8px',
          border: `2px solid ${isDarkTheme ? '#555' : '#ccc'}`,
          borderRadius: '4px',
          backgroundColor: isDarkTheme ? '#333' : '#fff',
          color: isDarkTheme ? '#fff' : '#000',
          boxSizing: 'border-box' as const,
          fontSize: '14px'
        }}
      />
      {(value || search) && (
        <span
          onClick={() => {
            onChange('');
            setSearch('');
            setShowDropdown(false);
          }}
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: 'pointer',
            color: isDarkTheme ? '#ccc' : '#666',
            fontSize: '20px',
            fontWeight: 'bold',
            lineHeight: '1',
            height: '20px',
          }}
        >
          ×
        </span>
      )}
      {!(value || search) && (
        <span
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
        </span>
      )}
      {showDropdown && filteredTotes.length > 0 && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            backgroundColor: isDarkTheme ? '#333' : '#fff',
            border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
            borderRadius: '4px',
            zIndex: 10000,
            width: dropdownPosition.width,
            maxHeight: '200px',
            overflowY: 'auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        >
          {filteredTotes.map((tote, index) => (
            <div
              key={tote}
              onClick={() => {
                onChange(tote);
                setShowDropdown(false);
                setSearch('');
                setSelectedIndex(-1);
              }}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: selectedIndex === index ? (isDarkTheme ? '#555' : '#e3f2fd') : 'transparent',
                color: isDarkTheme ? '#fff' : '#000',
                borderBottom: `1px solid ${isDarkTheme ? '#555' : '#eee'}`,
                fontSize: '14px',
              }}
              onMouseEnter={(e) => {
                if (selectedIndex !== index) {
                  e.currentTarget.style.backgroundColor = isDarkTheme ? '#444' : '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedIndex !== index) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {tote}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ToteDropdown;